
'use client';

import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { Loader2 } from 'lucide-react';

interface GenerateDocumentModalProps {
  caseId?: string;
  clientName?: string; // Adicionado para sugestão de nome de arquivo
}

interface Template {
  id: string;
  name: string;
}

export function GenerateDocumentModal({ caseId, clientName }: GenerateDocumentModalProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [templateId, setTemplateId] = useState<string>('');
  const [newFileName, setNewFileName] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (caseId && clientName) {
      setNewFileName(`Documento - ${clientName}`);
    } else {
      setNewFileName('');
    }
  }, [caseId, clientName]);

  useEffect(() => {
    if (isOpen) {
      const fetchTemplates = async () => {
        setLoadingTemplates(true);
        try {
          const response = await fetch('/api/google-drive/list-templates');
          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Erro ao carregar modelos.');
          }
          const data = await response.json();
          setTemplates(data.templates || []);
        } catch (error: any) {
          console.error('Erro ao buscar modelos:', error);
          toast({
            title: 'Erro',
            description: error.message || 'Não foi possível carregar os modelos de documento.',
            variant: 'destructive',
          });
        } finally {
          setLoadingTemplates(false);
        }
      };
      fetchTemplates();
    }
  }, [isOpen, toast]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    if (!templateId || !newFileName) {
      toast({
        title: 'Erro',
        description: 'Por favor, selecione um modelo e insira um nome de arquivo.',
        variant: 'destructive',
      });
      setIsLoading(false);
      return;
    }

    try {
      let apiUrl = '';
      let body = {};

      if (caseId) {
        apiUrl = `/api/cases/${caseId}/generate`;
        body = { templateId, newFileName };
      } else {
        apiUrl = '/api/documents/generate-generic';
        body = { templateId, newFileName };
      }

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erro ao gerar documento.');
      }

      const data = await response.json();
      window.open(data.documentUrl, '_blank');

      toast({
        title: 'Sucesso',
        description: 'Documento gerado com sucesso!',
      });
      setIsOpen(false);
    } catch (error: any) {
      console.error('Erro ao gerar documento:', error);
      toast({
        title: 'Erro',
        description: error.message || 'Ocorreu um erro ao gerar o documento.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline">Gerar Documento</Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Gerar Novo Documento</DialogTitle>
          <DialogDescription>
            Selecione um modelo e insira um nome para o novo documento.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="grid gap-4 py-4">
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="template" className="text-right">
              Modelo
            </Label>
            <Select onValueChange={setTemplateId} value={templateId} disabled={loadingTemplates}>
              <SelectTrigger className="col-span-3">
                {loadingTemplates ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> Carregando modelos...
                  </div>
                ) : (
                  <SelectValue placeholder="Selecione um modelo" />
                )}
              </SelectTrigger>
              <SelectContent>
                  {loadingTemplates ? (
                    <div className="flex items-center justify-center p-4">
                      <Loader2 className="w-6 h-6 animate-spin text-gray-500" />
                    </div>
                  ) : templates.length === 0 ? (
                    <div className="text-center text-gray-500 p-4">Nenhum modelo encontrado.</div>
                  ) : (
                    templates.map((template) => (
                      <SelectItem key={template.id} value={template.id}>
                        {template.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="name" className="text-right">
              Nome do Arquivo
            </Label>
            <Input
              id="name"
              value={newFileName}
              onChange={(e) => setNewFileName(e.target.value)}
              className="col-span-3"
              placeholder={caseId ? `Ex: Contrato - ${clientName || '[Nome do Cliente]'}` : 'Ex: Petição Inicial'}
            />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={isLoading || loadingTemplates}>
              {isLoading ? 'Gerando...' : 'Gerar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
